<template>
	<div class="storage-popup">
		<ScrollFade>
			<template #scrollContent>
				<div
					class="storage-popup-list"
					v-if="storedBuildings.length > 0"
				>
					<div
						v-for="building in storedBuildings"
						:key="building.name"
						class="storage-tile-box"
						:class="{
							'storage-tile-box--premium': building.isPremium,
							'storage-tile-box--event': building.isEvent,
						}"
						:data-event-type="building.eventType"
						@click="onClick(building.name as FrameName)"
					>
						<div
							class="storage-tile-box__image"
							:class="`icon-${building.image}-60`"
						/>
						<div class="storage-tile-box__content">
							<span class="storage-tile-box__heading">
								{{ building.displayName }}
							</span>
							<span
								class="storage-tile-box__description"
								v-html="
									switchParagraphFormat(
										building.description ?? '',
									)
								"
							/>
							<span class="storage-tile-box__amount">
								{{ translate("ui.in_inventory") }}
								<span class="storage-tile-box__amount--bold">
									{{ `x${building.amount}` }}
								</span>
							</span>
						</div>
					</div>
				</div>
			</template>
		</ScrollFade>
	</div>
</template>

<script lang="ts" setup>
import ScrollFade from "@/components/ScrollFade.vue";
import { isTemporaryEventBuildingActive } from "@/event";
import type { FrameName } from "@/game/entities/config";
import {
	tilerepoChristmasEventBuildingsFrameNames,
	tilerepoEasterEventBuildingsFrameNames,
	tilerepoEventBuildingsFrameNames,
	tilerepoFirefestEventBuildingsFrameNames,
	tilerepoFishingEventBuildingsFrameNames,
	tilerepoHalloweenEventBuildingsFrameNames,
	tilerepoHuntingEventBuildingsFrameNames,
	tilerepoPiratesEventBuildingsFrameNames,
	tilerepoPremiumBuildings,
	tilerepoRemovableEventBuildingsFrameNames,
	tilerepoSolarFairEventBuildingsFrameNames,
} from "@/game/entities/config";
import useTileRepository from "@/hooks/useTileRepository";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useQuickToolStore from "@/store/useQuickToolStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore";
import { EventCategory } from "@/types/ui";
import { QuickToolActions } from "@/types/upgrades";
import { switchParagraphFormat } from "@/utils/switchParagraphFormat";
import { getTileIconName } from "@/utils/tileIconName";
import { translate } from "@/utils/translate";
import { computed, markRaw } from "vue";

interface StorageBuilding {
	name: string;
	amount: number;
	image: string;
	displayName: string;
	description: string;
	isPremium: boolean;
	isEvent: boolean;
	eventType?: EventCategory;
}

interface BuildingTileInfo {
	image: string;
	displayName: string;
	description: string;
}

const emit = defineEmits(["closePopup"]);

const tilerepo = markRaw(useTileRepository());
const buildingsStorageStore = useBuildingsStorageStore();
const { setBuilding, setAction, setActive, setShow, setError } =
	useQuickToolStore();

const shouldHideBuilding = (frameName: FrameName): boolean =>
	tilerepoRemovableEventBuildingsFrameNames.has(frameName) &&
	!isTemporaryEventBuildingActive(frameName);

const getBuildingTileInfo = (frameName: string): BuildingTileInfo => {
	const config = tilerepo.createEntityByFrameName(frameName as FrameName);
	if (!config) {
		console.error(`Building config not found for frameName: ${frameName}`);
		return {
			image: "",
			displayName: frameName,
			description: "",
		};
	}
	return {
		image: getTileIconName(frameName as FrameName),
		displayName: config.name || frameName,
		description: config.description || "",
	};
};

const buildingEventType = (frameName: FrameName): EventCategory | undefined => {
	if (tilerepoEasterEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.EASTER;
	}
	if (tilerepoChristmasEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.CHRISTMAS;
	}
	if (tilerepoFishingEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.FISHING;
	}
	if (tilerepoFirefestEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.FIREFEST;
	}
	if (tilerepoSolarFairEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.SOLAR;
	}
	if (tilerepoHuntingEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.HUNTING;
	}
	if (tilerepoHalloweenEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.HALLOWEEN;
	}
	if (tilerepoPiratesEventBuildingsFrameNames.has(frameName)) {
		return EventCategory.PIRATES;
	}

	return undefined;
};

const storedBuildings = computed((): Array<StorageBuilding> => {
	const eventBuildings = Object.entries(
		buildingsStorageStore.getAllBuildings(),
	).filter(
		([frameName, value]: [string, number]): boolean =>
			Number.isFinite(value) &&
			value > 0 &&
			!shouldHideBuilding(frameName as FrameName) &&
			tilerepoEventBuildingsFrameNames.has(frameName as FrameName),
	);

	const nonEventBuildings = Object.entries(
		buildingsStorageStore.getAllBuildings(),
	).filter(
		([frameName, value]: [string, number]): boolean =>
			Number.isFinite(value) &&
			value > 0 &&
			!shouldHideBuilding(frameName as FrameName) &&
			!tilerepoEventBuildingsFrameNames.has(frameName as FrameName),
	);

	const pole = [...eventBuildings, ...nonEventBuildings];
	return pole
		.filter(([, value]: [string, number]): boolean => value > 0)
		.map(([frameName, value]: [string, number]): StorageBuilding => {
			const info = getBuildingTileInfo(frameName);
			return {
				name: frameName,
				amount: value,
				image: info.image,
				displayName: info.displayName,
				description: info.description,
				isPremium: tilerepoPremiumBuildings.has(frameName as FrameName),
				isEvent: tilerepoEventBuildingsFrameNames.has(
					frameName as FrameName,
				),
				eventType: buildingEventType(frameName as FrameName),
			};
		});
});

const onClick = (frameName: FrameName): void => {
	// Cancel pending unit move so inventory place cannot inherit that unit's HP
	useSiegeSystemStore().setMovingUnitsIndex(null);

	setBuilding(frameName);
	setAction(QuickToolActions.BUILD);
	setActive(true);
	setShow(true);
	setError(false);

	emit("closePopup");
};
</script>

<style scoped lang="scss">
.storage-popup {
	position: relative;
	@include flex-col-start;
	width: 100%;
	overflow: hidden;
}

.storage-popup-list {
	display: flex;
	flex-direction: column;
	padding: 0 0.5rem 0;
}

.storage-tile-box {
	display: flex;
	position: relative;
	align-items: flex-start;
	margin-bottom: 0.5rem;
	box-shadow: inset 0 0 0.75rem 0 #1d1e21;
	border-width: 0.05rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #61667f, #787c8b);
	border-image-slice: 1;
	background-image: linear-gradient(to top, #3b3c48, #595d6d);
	padding: 0.4rem;
	min-width: 20rem;
	min-height: 5.5rem;

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		z-index: 0;
		mix-blend-mode: color-burn;
		border-image-source: linear-gradient(to top, #3f5941, #5e7a61);
		@include background(
			url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
			40%,
			center,
			repeat
		);
		width: 100%;
		height: 100%;
		content: "";
	}

	&--premium {
		border-image-source: linear-gradient(to top, #9e68b7, #b080cb);
		background-image: linear-gradient(to top, #543464, #8d59a4);

		&::before {
			@include background(
				url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
				40%,
				center,
				repeat
			);
		}
	}

	&--event {
		&::before {
			@include background(
				url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
				40%,
				center,
				repeat
			);
		}
		&[data-event-type="christmas"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(111, 14, 18, 1) 0%,
				rgba(165, 29, 35, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(64, 6, 9, 1) 0%,
				rgba(102, 15, 20, 1) 100%
			);
		}
		&[data-event-type="easter"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(90, 111, 8, 1) 0%,
				rgba(120, 149, 8, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(34, 42, 4, 1) 0%,
				rgba(61, 76, 3, 1) 100%
			);
		}
		&[data-event-type="fishing"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(15, 86, 112, 1) 0%,
				rgba(30, 129, 166, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(6, 48, 64, 1) 0%,
				rgba(15, 79, 102, 1) 100%
			);
		}
		&[data-event-type="firefest"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(111, 33, 15, 1) 0%,
				rgba(175, 64, 35, 1) 100%
			);
		}
		&[data-event-type="solarfair"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(101, 60, 17, 1) 0%,
				rgba(181, 113, 38, 1) 100%
			);
		}
		&[data-event-type="hunting"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(15, 112, 20, 1) 0%,
				rgba(25, 150, 32, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(17, 63, 17, 1) 0%,
				rgba(35, 114, 35, 1) 100%
			);
		}
		&[data-event-type="halloween"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(59, 29, 130, 1) 0%,
				rgba(97, 52, 201, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(30, 17, 60, 1) 0%,
				rgba(75, 46, 141, 1) 100%
			);
		}
		&[data-event-type="pirates"] {
			border-image-source: linear-gradient(
				0deg,
				rgba(24, 66, 114, 1) 0%,
				rgba(34, 93, 158, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(10, 35, 64, 1) 0%,
				rgba(26, 71, 122, 1) 100%
			);
		}
	}

	&__image {
		flex-shrink: 0;
		margin: 0 0.8rem;
	}

	&__content {
		display: flex;
		position: relative;
		flex-direction: column;
		padding: 0.3rem;
		width: 100%;
		height: 100%;
	}

	&__heading {
		color: #f2dcb5;
		font-weight: 800;
		font-size: 1.1rem;
		line-height: 1.2rem;
		text-shadow: 0.07rem 0.07rem 0.1rem rgba(0, 0, 0, 0.6);
	}

	&__description {
		margin-bottom: 1.5rem;
		min-height: 1.1rem;
		color: #f2dcb5;
		font-size: 0.85rem;
		line-height: 1.1rem;
	}

	&__amount {
		position: absolute;
		right: 0.5rem;
		bottom: 0rem;
		color: #f2dcb5;
		font-size: 1rem;
		text-align: right;
	}

	&__amount--bold {
		margin-left: 0.25rem;
		color: #ffe08a;
		font-weight: 900;
	}
}

.storage-popup-empty {
	padding: 1rem 0;
	color: #888;
	text-align: center;
}
</style>
